import React, { useEffect, useReducer, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import Spinner from "../Components/Spinner";
import { ToastComponent } from "../Components/ToastComponent";
import DeleteModal from "../Components/Modals/Delete-modal";
import EmptyData from "../../components/EmptyData";

import { transportsClientsService } from "../../Services/transportsClients.service";
import { transportsPathsService } from "../../Services/transportsPaths.service";
import { vehicleGroupService } from "../../Services/vehicle.group.service";
import { vehicleService } from "../../Services/vehicle.service";
import { paginationService } from "../../Services/pagination.service";

import { SiAddthis } from "react-icons/si";
import { BiEditAlt, BiTrash } from "react-icons/bi";
import {
  GridComponent,
  Page,
  Inject,
  ColumnDirective,
  ColumnsDirective,
  Filter,
} from "@syncfusion/ej2-react-grids";

const DAY_FIELDS = [
  { key: "sunday", labelKey: "SUNDAY" },
  { key: "monday", labelKey: "MONDAY" },
  { key: "tuesday", labelKey: "TUESDAY" },
  { key: "wednesday", labelKey: "WEDNESDAY" },
  { key: "thursday", labelKey: "THURSDAY" },
  { key: "friday", labelKey: "FRIDAY" },
  { key: "saturday", labelKey: "SATURDAY" },
];

const formInitialValues = {
  pathId: 0,
  name: "",
  purpose: "",
  sunday: false,
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false,
  startTime: "",
  endTime: "",
  vehicleGroupId: "",
  vehicleId: "",
};

const normalizeFlag = (value) => value === "S" || value === true;
const flagPayloadValue = (value) => (value ? "S" : "N");

const toTimeInputValue = (value) => {
  if (!value) return "";
  return value.length >= 5 ? value.slice(0, 5) : value;
};

const toApiTimeValue = (value) => {
  if (!value) return "00:00:00";
  return value.length === 5 ? `${value}:00` : value;
};

const formReducer = (state, event) => {
  if (event.reset) {
    return formInitialValues;
  }
  return {
    ...state,
    [event.name]: event.value,
  };
};

export default function FretamentoPaths() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: "title", value: t("TRANSPORT_PATHS.PAGE_TITLE") });
  }, [dispatch, t]);

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");

  const [groups, setGroups] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const [data, setData] = useState([]);
  const [formData, setFormData] = useReducer(formReducer, formInitialValues);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState({});
  const [toastParams, setToastParams] = useState("");
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [isDeleting, setIsDeleting] = useState({ show: false, itemName: "" });

  // Campos ainda sem API (endpoint em desenvolvimento em outra task)
  const [reprocessStartDate, setReprocessStartDate] = useState("");
  const [reprocessEndDate, setReprocessEndDate] = useState("");

  // Altura e paginação dinâmicos
  const [pageSize, setPageSize] = useState(15);
  const gridAnchorRef = useRef(null);

  const ROW_HEIGHT = 30;
  const HEADER_PAGER_BUDGET = 100;
  const BOTTOM_MARGIN = 30;
  const MIN_PAGE = 5;
  const MAX_PAGE = 100;

  const showFeatureNotAvailableToast = () => {
    setToastParams(
      JSON.stringify({
        show: true,
        title: t("COMMON.ATTENTION"),
        body: t("TRANSPORT_PATHS.FEATURE_NOT_AVAILABLE"),
        color: "Warning",
      })
    );
  };

  const handleCloseModal = () => {
    setIsDeleting({ show: false, itemName: "" });
  };

  useEffect(() => {
    const loadCompanies = async () => {
      const response = await transportsClientsService.fetchClients();
      setCompanies(Array.isArray(response) ? response : []);
    };
    const loadGroups = async () => {
      const response = await vehicleGroupService.fetchGroups();
      setGroups(Array.isArray(response) ? response : []);
    };
    loadCompanies();
    loadGroups();
  }, []);

  const initialize = async (companyId) => {
    if (!companyId) {
      setData([]);
      return;
    }

    setIsPageLoading(true);
    try {
      const response = await transportsPathsService.fetchPaths(companyId);

      if (!response || (Array.isArray(response) && response.length === 0)) {
        setErrorMessage(t("TRANSPORT_PATHS.NO_RECORDS_FOUND"));
        setData([]);
        return;
      }

      setData(Array.isArray(response) ? response : [response]);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(t("TRANSPORT_PATHS.LOAD_ERROR"));
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    setSelectedCompanyId(companyId);
    setShowForm(false);
    initialize(companyId);
  };

  const fetchVehiclesForGroup = async (groupId) => {
    const response = await vehicleService.fetchVehicles(groupId || 0);
    setVehicles(Array.isArray(response) ? response : []);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ name, value: type === "checkbox" ? checked : value });
  };

  const handleGroupChange = (e) => {
    const groupId = e.target.value;
    setFormData({ name: "vehicleGroupId", value: groupId });
    setFormData({ name: "vehicleId", value: "" });
    fetchVehiclesForGroup(groupId);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = t("TRANSPORT_PATHS.NAME_REQUIRED");
    }
    if (!formData?.purpose) {
      newErrors.purpose = t("TRANSPORT_PATHS.PURPOSE_REQUIRED");
    }
    if (!formData?.vehicleId) {
      newErrors.vehicleId = t("TRANSPORT_PATHS.VEHICLE_REQUIRED");
    }

    return newErrors;
  };

  const onNew = () => {
    setFormData({ reset: true });
    setEditingItem({});
    setErrors({});
    fetchVehiclesForGroup("");
    setShowForm(true);
  };

  const onEdit = async (item) => {
    setEditingItem(item);
    setFormData({ name: "pathId", value: item.pathId });
    setFormData({ name: "name", value: item.name });
    setFormData({ name: "purpose", value: item.purpose });
    setFormData({ name: "sunday", value: normalizeFlag(item.sunday) });
    setFormData({ name: "monday", value: normalizeFlag(item.monday) });
    setFormData({ name: "tuesday", value: normalizeFlag(item.tuesday) });
    setFormData({ name: "wednesday", value: normalizeFlag(item.wednesday) });
    setFormData({ name: "thursday", value: normalizeFlag(item.thursday) });
    setFormData({ name: "friday", value: normalizeFlag(item.friday) });
    setFormData({ name: "saturday", value: normalizeFlag(item.saturday) });
    setFormData({ name: "startTime", value: toTimeInputValue(item.startTime) });
    setFormData({ name: "endTime", value: toTimeInputValue(item.endTime) });
    setFormData({ name: "vehicleGroupId", value: item.vehicleGroupId ?? "" });
    setFormData({ name: "vehicleId", value: item.vehicleId ?? "" });
    setErrors({});
    await fetchVehiclesForGroup(item.vehicleGroupId);
    setShowForm(true);
  };

  const onClear = () => {
    setFormData({ reset: true });
    setErrors({});
    fetchVehiclesForGroup("");
  };

  const onCancel = () => {
    setFormData({ reset: true });
    setEditingItem({});
    setErrors({});
    setShowForm(false);
  };

  const onDelete = (item) => {
    setIsDeleting({
      show: true,
      itemName: item.name,
      itemRemoveId: item.pathId,
    });
  };

  const handleRemove = async (pathId) => {
    try {
      const response = await transportsPathsService.deletePath(selectedCompanyId, pathId);
      if (!response) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.ERROR"),
            body: t("TRANSPORT_PATHS.DELETE_ERROR"),
            color: "Danger",
          })
        );
        return;
      }

      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.SUCCESS"),
          body: t("TRANSPORT_PATHS.DELETE_SUCCESS"),
          color: "Success",
        })
      );
    } catch (error) {
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.ERROR"),
          body: t("TRANSPORT_PATHS.DELETE_ERROR"),
          color: "Danger",
        })
      );
    } finally {
      handleCloseModal();
      initialize(selectedCompanyId);
    }
  };

  const onSave = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSaving(true);
    const isEditing = editingItem?.pathId > 0;

    const payload = {
      PathId: editingItem?.pathId || 0,
      CompanyId: Number(selectedCompanyId),
      Name: formData.name ?? "",
      Purpose: formData.purpose ?? "",
      Sunday: flagPayloadValue(formData.sunday),
      Monday: flagPayloadValue(formData.monday),
      Tuesday: flagPayloadValue(formData.tuesday),
      Wednesday: flagPayloadValue(formData.wednesday),
      Thursday: flagPayloadValue(formData.thursday),
      Friday: flagPayloadValue(formData.friday),
      Saturday: flagPayloadValue(formData.saturday),
      StartTime: toApiTimeValue(formData.startTime),
      EndTime: toApiTimeValue(formData.endTime),
      VehicleId: Number(formData.vehicleId),
    };

    try {
      const response = await transportsPathsService.savePath(payload);
      if (!response) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.ERROR"),
            body: t("TRANSPORT_PATHS.SAVE_ERROR"),
            color: "Danger",
          })
        );
        return;
      }

      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.SUCCESS"),
          body: isEditing
            ? t("TRANSPORT_PATHS.SAVE_SUCCESS_EDIT")
            : t("TRANSPORT_PATHS.SAVE_SUCCESS_NEW"),
          color: "Success",
        })
      );
    } catch (error) {
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.ERROR"),
          body: t("TRANSPORT_PATHS.SAVE_ERROR"),
          color: "Danger",
        })
      );
    } finally {
      setIsSaving(false);
      setShowForm(false);
      setFormData({ reset: true });
      setEditingItem({});
      setErrors({});
      initialize(selectedCompanyId);
    }
  };

  // Debounce simples para resize + recálculo ao terminar loading
  useEffect(() => {
    if (!isPageLoading) paginationService.calculateGridMetrics(
      gridAnchorRef,
      BOTTOM_MARGIN,
      HEADER_PAGER_BUDGET,
      ROW_HEIGHT, MIN_PAGE,
      MAX_PAGE,
      data,
      window.innerHeight,
      setPageSize
    );

    let resizeTimeout = null;
    const onResize = () => {
      if (resizeTimeout) window.clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => paginationService.calculateGridMetrics(
        gridAnchorRef,
        BOTTOM_MARGIN,
        HEADER_PAGER_BUDGET,
        ROW_HEIGHT, MIN_PAGE,
        MAX_PAGE,
        data,
        window.innerHeight,
        setPageSize
      ), 50);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (resizeTimeout) window.clearTimeout(resizeTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageLoading]);

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      <DeleteModal
        itemName={isDeleting.itemName}
        itemRemoveId={isDeleting.itemRemoveId}
        callBackRemove={handleRemove}
        show={isDeleting.show}
        handleClose={handleCloseModal}
      />

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div className="row">
          <label className="col-sm-2 col-form-label">
            {t("TRANSPORT_PATHS.COMPANY")}
          </label>
          <div className="col-sm-6">
            <select
              className="form-control"
              value={selectedCompanyId}
              onChange={handleCompanyChange}
            >
              <option value="">{t("TRANSPORT_PATHS.SELECT_COMPANY")}</option>
              {companies.map((company) => (
                <option key={company.companyId} value={company.companyId}>
                  {company.companyName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div ref={gridAnchorRef} />

      {!selectedCompanyId ? (
        <p className="mt-2">{t("TRANSPORT_PATHS.SELECT_COMPANY_MESSAGE")}</p>
      ) : isPageLoading ? (
        <Spinner />
      ) : (
        <div style={showForm ? { display: "none" } : { display: "" }}>
          <div className="d-flex justify-content-between">
            <div className="p-2 card-header">
              <h5>{t("TRANSPORT_PATHS.LIST_TITLE")}</h5>
            </div>
            <div className="p-2"></div>
            <div className="p-2">
              <SiAddthis className="icon-new" onClick={onNew} />
            </div>
          </div>

          <div className="card">
            <GridComponent
              dataSource={data}
              allowPaging={true}
              allowFiltering={true}
              filterSettings={{ type: "Menu" }}
              pageSettings={{ pageSize }}
              rowHeight={ROW_HEIGHT}
              height={"auto"}
              locale="pt-BR"
              emptyRecordTemplate={() => <EmptyData />}
              actionComplete={() => {
                setTimeout(() => paginationService.calculateGridMetrics(
                  gridAnchorRef,
                  BOTTOM_MARGIN,
                  HEADER_PAGER_BUDGET,
                  ROW_HEIGHT, MIN_PAGE,
                  MAX_PAGE,
                  data,
                  window.innerHeight,
                  setPageSize
                ), 50);
              }}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="name"
                  headerText={t("TRANSPORT_PATHS.LINE_COLUMN")}
                  width="180"
                />
                <ColumnDirective
                  headerText={t("TRANSPORT_PATHS.PURPOSE")}
                  width="120"
                  template={(props) =>
                    props.purpose === "E"
                      ? t("TRANSPORT_PATHS.PURPOSE_ENTRY")
                      : props.purpose === "S"
                      ? t("TRANSPORT_PATHS.PURPOSE_EXIT")
                      : ""
                  }
                />
                <ColumnDirective
                  field="startTime"
                  headerText={t("TRANSPORT_PATHS.START_COLUMN")}
                  width="110"
                />
                <ColumnDirective
                  field="endTime"
                  headerText={t("TRANSPORT_PATHS.END_COLUMN")}
                  width="110"
                />
                <ColumnDirective
                  field="weekSchedule"
                  headerText={t("TRANSPORT_PATHS.WEEK_COLUMN")}
                  width="140"
                />
                <ColumnDirective
                  field="vehiclePlate"
                  headerText={t("TRANSPORT_PATHS.VEHICLE_COLUMN")}
                  width="160"
                />
                <ColumnDirective
                  headerText={t("TRANSPORT_PATHS.ACTIONS")}
                  width="120"
                  template={(props) => (
                    <div style={{ display: "flex", gap: "8px" }}>
                      <BiEditAlt className="icon-button" onClick={() => onEdit(props)} />
                      <BiTrash className="icon-button" onClick={() => onDelete(props)} />
                    </div>
                  )}
                />
              </ColumnsDirective>
              <Inject services={[Page, Filter]} />
            </GridComponent>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-2">
            <button
              type="button"
              className="btn btn-outline-success"
              onClick={showFeatureNotAvailableToast}
            >
              {t("TRANSPORT_PATHS.EXPORT_FILE")} (Excel)
            </button>
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={showFeatureNotAvailableToast}
            >
              {t("TRANSPORT_PATHS.EXPORT_FILE")} (PDF)
            </button>
          </div>

          {errorMessage && <p className="mt-4">{errorMessage}</p>}
        </div>
      )}

      <div
        className="row py-3"
        style={!showForm ? { display: "none" } : { display: "flex", flexWrap: "wrap" }}
      >
        <div className="col-12 col-lg-9">
          <div className="card">
            <h5 className="card-header">
              {editingItem?.pathId > 0
                ? t("TRANSPORT_PATHS.EDIT_TITLE")
                : t("TRANSPORT_PATHS.NEW_TITLE")}
            </h5>
            <div className="card-header">
              <div className="row mb-3">
                <label className="col-12 col-md-2 col-form-label">
                  {t("TRANSPORT_PATHS.NAME")}
                </label>
                <div className="col-12 col-md-4">
                  <input
                    type="text"
                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                    name="name"
                    value={formData.name || ""}
                    onChange={handleChange}
                    maxLength={150}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>
                <label className="col-12 col-md-2 col-form-label">
                  {t("TRANSPORT_PATHS.PURPOSE")}
                </label>
                <div className="col-12 col-md-4">
                  <select
                    className={`form-control ${errors.purpose ? "is-invalid" : ""}`}
                    name="purpose"
                    value={formData.purpose || ""}
                    onChange={handleChange}
                  >
                    <option value="">{t("TRANSPORT_PATHS.SELECT_PURPOSE")}</option>
                    <option value="E">{t("TRANSPORT_PATHS.PURPOSE_ENTRY")}</option>
                    <option value="S">{t("TRANSPORT_PATHS.PURPOSE_EXIT")}</option>
                  </select>
                  {errors.purpose && (
                    <div className="invalid-feedback d-block">{errors.purpose}</div>
                  )}
                </div>
              </div>

              <div className="row mb-3">
                <label className="col-12 col-md-2 col-form-label">
                  {t("TRANSPORT_PATHS.DAYS")}
                </label>
                <div className="col-12 col-md-10 d-flex flex-wrap gap-3">
                  {DAY_FIELDS.map((day) => (
                    <div key={day.key} className="d-flex align-items-center gap-1">
                      <input
                        type="checkbox"
                        id={day.key}
                        name={day.key}
                        checked={Boolean(formData[day.key])}
                        onChange={handleChange}
                      />
                      <label htmlFor={day.key} className="mb-0">
                        {t(`TRANSPORT_PATHS.${day.labelKey}`)}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="row mb-3">
                <label className="col-12 col-md-2 col-form-label">
                  {t("TRANSPORT_PATHS.START_TIME")}
                </label>
                <div className="col-12 col-md-4">
                  <input
                    type="time"
                    className="form-control"
                    name="startTime"
                    value={formData.startTime || ""}
                    onChange={handleChange}
                  />
                </div>
                <label className="col-12 col-md-2 col-form-label">
                  {t("TRANSPORT_PATHS.END_TIME")}
                </label>
                <div className="col-12 col-md-4">
                  <input
                    type="time"
                    className="form-control"
                    name="endTime"
                    value={formData.endTime || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <label className="col-12 col-md-2 col-form-label">
                  {t("TRANSPORT_PATHS.GROUP")}
                </label>
                <div className="col-12 col-md-4">
                  <select
                    className="form-control"
                    name="vehicleGroupId"
                    value={formData.vehicleGroupId || ""}
                    onChange={handleGroupChange}
                  >
                    <option value="">{t("TRANSPORT_PATHS.ALL_GROUPS")}</option>
                    {groups.map((group) => (
                      <option key={group.vehicleGroupId} value={group.vehicleGroupId}>
                        {group.vehicleGroupName}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="col-12 col-md-2 col-form-label">
                  {t("TRANSPORT_PATHS.VEHICLE")}
                </label>
                <div className="col-12 col-md-4">
                  <select
                    className={`form-control ${errors.vehicleId ? "is-invalid" : ""}`}
                    name="vehicleId"
                    value={formData.vehicleId || ""}
                    onChange={handleChange}
                  >
                    <option value="">{t("TRANSPORT_PATHS.SELECT_VEHICLE")}</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle.vehicleId} value={vehicle.vehicleId}>
                        {vehicle.plate}
                      </option>
                    ))}
                  </select>
                  {errors.vehicleId && (
                    <div className="invalid-feedback d-block">{errors.vehicleId}</div>
                  )}
                </div>
              </div>

              <div className="row justify-content-end" style={{ textAlign: "end" }}>
                <div className="col-ls-8 col-md-8 col-xs-12 mb-3">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ marginRight: "10px" }}
                    onClick={onClear}
                  >
                    {t("TRANSPORT_PATHS.CLEAR")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-warning"
                    style={{ marginRight: "10px" }}
                    onClick={onCancel}
                  >
                    {t("TRANSPORT_PATHS.CANCEL")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={onSave}
                    disabled={isSaving}
                  >
                    {t("TRANSPORT_PATHS.SAVE")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reprocessamento (endpoint em outra task) */}
        <div className="col-12 col-lg-3 mt-3 mt-lg-0">
          <div className="card" style={{ padding: 16 }}>
            <div className="row mb-3">
              <label className="col-sm-12 col-form-label">
                {t("TRANSPORT_PATHS.START_DATE")}
              </label>
              <div className="col-sm-12">
                <input
                  type="date"
                  className="form-control"
                  value={reprocessStartDate}
                  onChange={(e) => setReprocessStartDate(e.target.value)}
                />
              </div>
            </div>
            <div className="row mb-3">
              <label className="col-sm-12 col-form-label">
                {t("TRANSPORT_PATHS.END_DATE")}
              </label>
              <div className="col-sm-12">
                <input
                  type="date"
                  className="form-control"
                  value={reprocessEndDate}
                  onChange={(e) => setReprocessEndDate(e.target.value)}
                />
              </div>
            </div>
            <button
              type="button"
              className="btn btn-info text-white mb-2"
              onClick={showFeatureNotAvailableToast}
            >
              {t("TRANSPORT_PATHS.REPROCESS_DAILY")}
            </button>
            <button
              type="button"
              className="btn btn-info text-white"
              onClick={showFeatureNotAvailableToast}
            >
              {t("TRANSPORT_PATHS.REPROCESS_SUMMARY")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
